import React, { useState } from 'react';
import { Sheet, YStack, XStack } from 'tamagui';
import { ChevronDown } from '@tamagui/lucide-icons';
import { Virtuoso } from 'react-virtuoso';
// import { Ionicons } from '@expo/vector-icons';
import { useGetGroupsOfUserQuery } from '@cobu/api-client';
import { Group } from '@cobu/types';
import { GroupOption } from '../../schemas/groupEventSchema';

import { CobuText } from '@cobu/ui';
import { Strings } from '../../constants/Strings';

interface CobuGroupPickerProps {
  label?: string;
  value: GroupOption | null;
  onChange: (group: GroupOption) => void;
  error?: string;
  placeholder?: string;
}

const CobuGroupPicker: React.FC<CobuGroupPickerProps> = ({
  label,
  value,
  onChange,
  error,
  placeholder,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const gp = Strings.get().ui.groupPicker;

  // Fetch groups for the building
  const { data: groups = [], isLoading, error: fetchError } = useGetGroupsOfUserQuery();

  const showPicker = () => {
    setIsModalVisible(true);
  };

  const hidePicker = () => {
    setIsModalVisible(false);
  };

  const handleGroupSelect = (group: Group) => {
    const groupOption: GroupOption = {
      value: group.uuid,
      label: group.name,
    };
    onChange(groupOption);
    hidePicker();
  };

  const renderGroupItem = ({ item }: { item: Group }) => (
    <XStack
      p="$4"
      bc="$whiteTransparent70"
      bbw={1}
      bbc="$borderColor"
      ai="center"
      gap="$3"
      onPress={() => handleGroupSelect(item)}
    >
      <CobuText variant="body" f={1}>
        {item.name}
      </CobuText>
    </XStack>
  );

  const displayText = () => {
    if (isLoading) return gp.loading;
    if (fetchError) return gp.failedToLoad;
    if (value?.label) return value.label;
    return placeholder ?? gp.title;
  };

  return (
    <YStack gap="$1">
      {label && (
        <CobuText variant="label" color="$color" mb="$1">
          {label}
        </CobuText>
      )}

      <XStack
        br="$5"
        px="$4"
        py="$3"
        minH={48}
        ai="center"
        jc="space-between"
        onPress={showPicker}
        opacity={isLoading || !!fetchError ? 0.6 : 1}
        // Use explicit CSS border to match inputs
        style={{
          background: 'var(--whiteTransparent70)',
          border: `1px solid ${error ? 'var(--error)' : 'var(--borderColor)'}`,
          borderRadius: 12,
          cursor: 'pointer',
        }}
      >
        <CobuText
          variant="body"
          color={value?.label ? '$color' as any : '$colorMuted' as any}
          f={1}
        >
          {displayText()}
        </CobuText>
        <ChevronDown size={18} color="$colorMuted" />
      </XStack>

      {error && (
        <CobuText variant="caption" color="$error" mt="$1">
          {error}
        </CobuText>
      )}

      <Sheet
        modal
        open={isModalVisible}
        onOpenChange={setIsModalVisible}
        snapPoints={[90, 60, 30]}
        position={0}
        dismissOnSnapToBottom
      >
        <Sheet.Overlay style={{ backgroundColor: 'rgba(255, 255, 255, 1)', opacity: 1 }} />
        <Sheet.Handle />
        {/* Frame slides from bottom by default; force hard-coded white background */}
        <Sheet.Frame f={1} style={{ backgroundColor: '$white' }}>
          <XStack p="$4" bbw={1} bbc="$borderColor" ai="center" jc="space-between">
            <CobuText variant="heading">{gp.title}</CobuText>
            <XStack onPress={hidePicker} px="$2" py="$1">
              <CobuText variant="body" color="$colorMuted">{gp.closeLabel}</CobuText>
            </XStack>
          </XStack>

          {isLoading ? (
            <YStack f={1} ai="center" jc="center">
              <CobuText variant="body" color="$colorMuted">{gp.loading}</CobuText>
            </YStack>
          ) : fetchError ? (
            <YStack f={1} ai="center" jc="center" p="$4">
              <CobuText variant="body" color="$error" ta="center">{gp.failedToLoad}</CobuText>
            </YStack>
          ) : groups.length === 0 ? (
            <YStack f={1} ai="center" jc="center" p="$4">
              <CobuText variant="body" color="$colorMuted" ta="center">{gp.noGroupsFound}</CobuText>
            </YStack>
          ) : (
            <Virtuoso
              data={groups as any[]}
              itemContent={(index, item: any) => renderGroupItem({ item })}
              computeItemKey={(_, item: any) => item.uuid}
              style={{ flex: 1, width: '100%' }}
            />
          )}
        </Sheet.Frame>
      </Sheet>
    </YStack>
  );
};

export default CobuGroupPicker;